// src/pages/AISchoolsPage.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SchoolIcon from "@mui/icons-material/School";
import SecurityIcon from "@mui/icons-material/Security";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
// AutoStoriesIcon removed because it wasn’t used
import "aos/dist/aos.css";
import AOS from "aos";

/* 🔹 Standard footer link style (matches Landing & Subscriptions) */
const footerLinkStyle = {
  color: "white",
  textDecoration: "none",
};

/* 🔹 Base font for this page */
const baseFont = {
  fontFamily:
    "Montserrat, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const AISchoolsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        color: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        ...baseFont,
      }}
    >
      {/* 🔹 Standard header (aligned with Subscription & Landing pages) */}
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav
          style={{
            display: "flex",
            gap: "15px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => navigate("/help")}
            className="header-button"
          >
            Help
          </button>
          <button
            onClick={() => navigate("/subscriptions")}
            className="header-button"
          >
            View Plans
          </button>
          <button
            onClick={() => navigate("/")}
            className="header-button"
          >
            Home
          </button>
        </nav>
      </header>

      {/* MAIN CONTENT */}
      <main>
        {/* Hero */}
        <Container
          maxWidth="lg"
          style={{
            paddingTop: 40,
            paddingBottom: 40,
          }}
        >
          <Grid
            container
            spacing={4}
            alignItems="center"
            data-aos="fade-up"
          >
            <Grid item xs={12} md={6} style={{ color: "#f9fafb" }}>
              <Chip
                label="AI Literacy • Reading • Critical Thinking"
                size="small"
                style={{
                  backgroundColor: "rgba(59,130,246,0.32)",
                  color: "#e0f2fe",
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              />
              <Typography
                variant="h3"
                component="h1"
                style={{
                  fontWeight: 700,
                  color: "#f9fafb",
                  marginBottom: 12,
                  lineHeight: 1.2,
                }}
              >
                AI for Schools:
                <br />
                Safe, Smart Storytelling for the AI Learning in Schools
              </Typography>
              <Typography
                variant="body1"
                style={{
                  fontSize: "1rem",
                  lineHeight: 1.6,
                  color: "#e5e7eb",
                  marginBottom: 12,
                }}
              >
                YourStoryWorld helps pupils practise reading, writing and{" "}
                <strong>AI literacy</strong> in one place. Students learn how
                prompts work, how to question AI-generated text, and how to tell
                fiction from fact — all within a safe, teacher-controlled
                environment.
              </Typography>

              <Typography
                variant="body2"
                style={{
                  color: "#cbd5f5",
                  marginBottom: 20,
                }}
              >
                <strong>KS1:</strong> guided, sentence-level prompts and simple,
                phonics-friendly stories.{" "}
                <strong>KS2–KS3:</strong> fuller prompts, editing AI output, and
                structured critique of where the AI gets things wrong.
              </Typography>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                  onClick={() => navigate("/contact")}
                >
                  Book a School Demo
                </Button>
                <Button
                  variant="outlined"
                  style={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor: "#e5e7eb",
                    color: "#e5e7eb",
                  }}
                  onClick={() => navigate("/contact")}
                >
                  Talk to Us About AI in Your School
                </Button>
              </div>

              <Typography
                variant="caption"
                style={{
                  display: "block",
                  marginTop: 10,
                  color: "#cbd5f5",
                }}
              >
                Built for UK schools • Supports AI & digital literacy, English,
                and computing (KS1–KS3).
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                style={{
                  borderRadius: 16,
                  border: "1px solid rgba(148,163,184,0.35)",
                  boxShadow: "0 10px 30px rgba(15,23,42,0.65)",
                  background:
                    "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,64,175,0.92))",
                  color: "#e5e7eb",
                }}
              >
                <CardContent style={{ padding: 20 }}>
                  <Typography
                    variant="subtitle2"
                    style={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontSize: 11,
                      color: "#bfdbfe",
                      marginBottom: 6,
                    }}
                  >
                    Classroom snapshot
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#eff6ff",
                    }}
                  >
                    “Where did the AI get this wrong?”
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      marginBottom: 12,
                      lineHeight: 1.6,
                      color: "#e5e7eb",
                    }}
                  >
                    A class reads a YourStoryWorld passage, then pupils highlight
                    parts that are{" "}
                    <strong>
                      factually wrong or scientifically impossible
                    </strong>
                    . In groups, they rewrite those sentences and discuss why AI
                    sometimes “hallucinates”.
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: "#bfdbfe",
                      fontSize: 13,
                      marginBottom: 12,
                    }}
                  >
                    Outcome: Students practise{" "}
                    <strong>
                      critical reading, research, and AI scepticism
                    </strong>{" "}
                    — all while still enjoying the story.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Chip
                      size="small"
                      label="AI Literacy"
                      style={{
                        backgroundColor: "rgba(59,130,246,0.3)",
                        color: "#eff6ff",
                      }}
                    />
                    <Chip
                      size="small"
                      label="Reading Comprehension"
                      style={{
                        backgroundColor: "rgba(45,212,191,0.3)",
                        color: "#ecfeff",
                      }}
                    />
                    <Chip
                      size="small"
                      label="Upper KS2–KS3"
                      style={{
                        backgroundColor: "rgba(248,250,252,0.3)",
                        color: "#e5e7eb",
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Section 1: Why AI literacy matters */}
        <Container
          maxWidth="lg"
          style={{
            paddingBottom: 40,
            color: "#f9fafb",
          }}
        >
          <Typography
            variant="h4"
            align="center"
            style={{
              fontWeight: 700,
              marginBottom: 10,
              color: "#f9fafb",
            }}
          >
            Why AI Literacy Matters in the Classroom
          </Typography>
          <Typography
            variant="body1"
            align="center"
            style={{
              maxWidth: 700,
              margin: "0 auto 24px",
              color: "#e5e7eb",
            }}
          >
            UK schools are being asked to help pupils understand AI — what it can
            do, what it gets wrong, and how to use it safely. YourStoryWorld
            supports this without needing extra platforms or complicated tools.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                data-aos="fade-up"
                style={{
                  borderRadius: 14,
                  height: "100%",
                  boxShadow: "0 4px 14px rgba(148,163,184,0.3)",
                  backgroundColor: "rgba(255,255,255,0.97)",
                }}
              >
                <CardContent>
                  <LightbulbIcon style={{ color: "#f59e0b" }} />
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 600, marginTop: 8, color: "#0f172a" }}
                  >
                    Critical & curious readers
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 6, color: "#4b5563" }}
                  >
                    Pupils learn that AI can sound confident and still be wrong.
                    They practise questioning, checking and improving generated
                    text instead of copying it blindly.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                data-aos="fade-up"
                data-aos-delay="80"
                style={{
                  borderRadius: 14,
                  height: "100%",
                  boxShadow: "0 4px 14px rgba(148,163,184,0.3)",
                  backgroundColor: "rgba(255,255,255,0.97)",
                }}
              >
                <CardContent>
                  <SchoolIcon style={{ color: "#3b82f6" }} />
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 600, marginTop: 8, color: "#0f172a" }}
                  >
                    Links to curriculum & digital literacy
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 6, color: "#4b5563" }}
                  >
                    Supports English (reading and writing), computing and PSHE by
                    giving pupils structured experience of how AI responds to
                    different prompts.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                data-aos="fade-up"
                data-aos-delay="160"
                style={{
                  borderRadius: 14,
                  height: "100%",
                  boxShadow: "0 4px 14px rgba(148,163,184,0.3)",
                  backgroundColor: "rgba(255,255,255,0.97)",
                }}
              >
                <CardContent>
                  <SecurityIcon style={{ color: "#10b981" }} />
                  <Typography
                    variant="h6"
                    style={{ fontWeight: 600, marginTop: 8, color: "#0f172a" }}
                  >
                    Safe, guided AI use
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 6, color: "#4b5563" }}
                  >
                    AI is embedded inside a child-safe reading tool with filters,
                    dashboards, and teacher controls — not a general-purpose chat
                    bot on the open web.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Section 2: What pupils learn about AI */}
        <Container
          maxWidth="lg"
          style={{
            paddingTop: 20,
            paddingBottom: 40,
            color: "#f9fafb",
          }}
        >
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography
                variant="h4"
                style={{
                  fontWeight: 700,
                  marginBottom: 10,
                  color: "#f9fafb",
                }}
              >
                What Pupils Learn About AI While Using YourStoryWorld
              </Typography>
              <Typography
                variant="body1"
                style={{ marginBottom: 16, color: "#e5e7eb" }}
              >
                Every story session is also a mini AI lesson. Without extra apps,
                pupils naturally explore how AI responds to the instructions they
                give it.
              </Typography>

              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 600,
                  color: "#cbd5f5",
                  marginBottom: 6,
                }}
              >
                Key skills developed:
              </Typography>
              <ul
                style={{
                  paddingLeft: 18,
                  marginTop: 4,
                  marginBottom: 0,
                  color: "#e5e7eb",
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              >
                <li>Writing clear, structured prompts.</li>
                <li>Understanding that AI output is not always factual.</li>
                <li>Editing and improving AI-generated text.</li>
                <li>Testing how changing a prompt changes the output.</li>
                <li>Thinking about audience and purpose, not just content.</li>
              </ul>

              <Typography
                variant="body2"
                style={{
                  marginTop: 12,
                  color: "#cbd5f5",
                  fontSize: 13,
                }}
              >
                <strong>For KS1:</strong> prompts are simplified to choices and
                short phrases, with teachers modelling the AI thinking aloud.{" "}
                <br />
                <strong>For KS2–KS3:</strong> pupils write and refine their own
                prompts, then critique and improve the AI’s response.
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {[
                  "KS1: choose from a small set of guided prompts, then discuss what the story shows and what is imaginary.",
                  "KS2–KS3: start with a simple prompt, then refine it with more precise constraints (tone, genre, audience).",
                  "Highlight sentences that are unrealistic or inaccurate and rewrite them using research or prior knowledge.",
                  "Ask the AI to adapt the same story for a younger or older reader and compare the language.",
                  "Use built-in comprehension questions to check meaning, not just surface reading.",
                  "Record a reading of the story and reflect on fluency, expression, and how AI choices affect the text.",
                ].map((text, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Card
                      data-aos="fade-up"
                      data-aos-delay={idx * 40}
                      style={{
                        borderRadius: 12,
                        height: "100%",
                        border: "1px solid rgba(209,213,219,0.8)",
                        backgroundColor: "rgba(255,255,255,0.98)",
                      }}
                    >
                      <CardContent style={{ padding: 14 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                          }}
                        >
                          <CheckCircleIcon
                            fontSize="small"
                            style={{ color: "#22c55e", marginTop: 2 }}
                          />
                          <Typography
                            variant="body2"
                            style={{ color: "#374151", lineHeight: 1.5 }}
                          >
                            {text}
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>

        {/* Section 3: Classroom examples */}
        <Container
          maxWidth="lg"
          style={{ paddingTop: 10, paddingBottom: 40 }}
        >
          <Typography
            variant="h4"
            align="center"
            style={{
              fontWeight: 700,
              marginBottom: 10,
              color: "#f9fafb",
            }}
          >
            Practical AI Lesson Ideas Using YourStoryWorld
          </Typography>
          <Typography
            variant="body1"
            align="center"
            style={{
              maxWidth: 720,
              margin: "0 auto 18px",
              color: "#e5e7eb",
            }}
          >
            Use these ready-to-go examples to link AI, reading, and writing during
            English, computing or form time. All can be adapted for different year
            groups.
          </Typography>

          {/* Link to full Teacher Guide page */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Button
              variant="outlined"
              style={{
                borderColor: "#e5e7eb",
                color: "#e5e7eb",
                textTransform: "none",
                fontWeight: 500,
              }}
              onClick={() => navigate("/ai-for-schools-page")}
            >
              Open Full AI Teacher Guide
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card
                data-aos="fade-up"
                style={{
                  borderRadius: 14,
                  height: "100%",
                  backgroundColor: "rgba(255,255,255,0.96)",
                  border: "1px solid rgba(209,213,219,0.9)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: 4,
                    }}
                  >
                    1. AI vs Reality – Spot the Errors
                  </Typography>
                  <Typography variant="caption" style={{ color: "#6b7280" }}>
                    Upper KS2 / KS3 · Reading & science / geography
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 8, color: "#4b5563" }}
                  >
                    Pupils read a YourStoryWorld text about a real-world topic
                    (space, oceans, climate, etc.). In pairs, they:
                  </Typography>
                  <ul
                    style={{
                      marginTop: 6,
                      paddingLeft: 18,
                      color: "#4b5563",
                      fontSize: 13,
                    }}
                  >
                    <li>Underline anything that seems wrong or exaggerated.</li>
                    <li>Use research or prior knowledge to correct it.</li>
                    <li>Discuss why AI might have produced those mistakes.</li>
                  </ul>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                data-aos="fade-up"
                data-aos-delay="80"
                style={{
                  borderRadius: 14,
                  height: "100%",
                  backgroundColor: "rgba(255,255,255,0.96)",
                  border: "1px solid rgba(209,213,219,0.9)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: 4,
                    }}
                  >
                    2. Prompt Engineering Challenge
                  </Typography>
                  <Typography variant="caption" style={{ color: "#6b7280" }}>
                    KS2 / KS3 · Computing & English
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 8, color: "#4b5563" }}
                  >
                    Groups start with a simple prompt (e.g. “Write a story about a
                    haunted house”). They then:
                  </Typography>
                  <ul
                    style={{
                      marginTop: 6,
                      paddingLeft: 18,
                      color: "#4b5563",
                      fontSize: 13,
                    }}
                  >
                    <li>Add constraints (word count, age, genre, tone).</li>
                    <li>Compare the original and improved stories.</li>
                    <li>Share which prompts gave the clearest output and why.</li>
                  </ul>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                data-aos="fade-up"
                data-aos-delay="160"
                style={{
                  borderRadius: 14,
                  height: "100%",
                  backgroundColor: "rgba(255,255,255,0.96)",
                  border: "1px solid rgba(209,213,219,0.9)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: 4,
                    }}
                  >
                    3. Same Story, Different Audience
                  </Typography>
                  <Typography variant="caption" style={{ color: "#6b7280" }}>
                    KS1–KS3 variants · English & media literacy
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginTop: 8, color: "#4b5563" }}
                  >
                    Pupils generate one story, then ask the AI to:
                  </Typography>
                  <ul
                    style={{
                      marginTop: 6,
                      paddingLeft: 18,
                      color: "#4b5563",
                      fontSize: 13,
                    }}
                  >
                    <li>Rewrite it for a younger KS1 reader (shorter sentences).</li>
                    <li>Rewrite it as a news article or information text.</li>
                    <li>
                      Discuss how vocabulary, tone and structure change depending
                      on audience.
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* Section 4: Benefits for schools */}
        <Container
          maxWidth="lg"
          style={{ paddingTop: 10, paddingBottom: 40 }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6} style={{ color: "#f9fafb" }}>
              <Typography
                variant="h4"
                style={{
                  fontWeight: 700,
                  marginBottom: 10,
                  color: "#f9fafb",
                }}
              >
                Benefits for Teachers & Leaders
              </Typography>
              <Typography
                variant="body1"
                style={{ marginBottom: 16, color: "#e5e7eb" }}
              >
                YourStoryWorld is designed to fit into everyday reading practice without requiring extra platforms
              </Typography>

              <ul
                style={{
                  paddingLeft: 18,
                  color: "#e5e7eb",
                  lineHeight: 1.7,
                  fontSize: 14,
                }}
              >
                <li>
                  <strong>Ready in minutes</strong> – no complex setup; teachers
                  use existing devices.
                </li>
                <li>
                  <strong>Structured prompts</strong> – pupils work within guided
                  fields, not open-ended AI chat.
                </li>
                <li>
                  <strong>Safe for children</strong> – filters, content rules and
                  moderation to block harmful themes.
                </li>
                <li>
                  <strong>Clear analytics</strong> – dashboards show reading
                  frequency, genres, comprehension and voice recordings.
                </li>
                <li>
                  <strong>Works at home and school</strong> – same pupil account
                  across classroom and family use.
                </li>
              </ul>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                data-aos="fade-left"
                style={{
                  borderRadius: 16,
                  paddingTop: 6,
                  boxShadow: "0 6px 18px rgba(148,163,184,0.4)",
                  backgroundColor: "rgba(255,255,255,0.97)",
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    style={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontSize: 11,
                      color: "#6b7280",
                      marginBottom: 6,
                    }}
                  >
                    Safeguarding & control
                  </Typography>
                  <Typography
                    variant="h6"
                    style={{
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#0f172a",
                    }}
                  >
                    Designed for safe AI use in schools
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ marginBottom: 10, color: "#4b5563" }}
                  >
                    YourStoryWorld has been built around UK safeguarding
                    expectations. Stories are automatically filtered to block:
                  </Typography>
                  <ul
                    style={{
                      paddingLeft: 18,
                      color: "#4b5563",
                      fontSize: 13,
                      marginBottom: 8,
                    }}
                  >
                    <li>Violent or graphic content</li>
                    <li>Explicit, romantic or adult themes</li>
                    <li>Discriminatory or harmful language</li>
                    <li>Dangerous or unsafe behaviour</li>
                  </ul>
                  <Typography
                    variant="body2"
                    style={{ color: "#6b7280", fontSize: 13 }}
                  >
                    Teachers remain in control of usage, and admin dashboards give
                    leaders a clear view of engagement and usage.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        {/* CTA band */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(37,99,235,0.96), rgba(59,130,246,0.98))",
            padding: "32px 0",
            marginTop: 10,
          }}
        >
          <Container maxWidth="lg" style={{ color: "#eff6ff" }}>
            <Grid
              container
              spacing={3}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h5"
                  style={{
                    fontWeight: 700,
                    color: "#eff6ff",
                    marginBottom: 6,
                  }}
                >
                  Ready to bring safe, meaningful AI into your classrooms?
                </Typography>
                <Typography
                  variant="body2"
                  style={{ color: "#dbeafe", maxWidth: 600 }}
                >
                  Book a short demo and see how YourStoryWorld can support reading
                  engagement, writing, and AI literacy across your school — with
                  built-in safeguarding and analytics for staff.
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: "#facc15",
                    color: "#1f2937",
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                  onClick={() => navigate("/contact")}
                >
                  Book a School Demo
                </Button>
                
              </Grid>
            </Grid>
          </Container>
        </div>
      </main>

      {/* 🔹 Standard footer (matches Landing & Subscriptions) */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        {/* Company & rights */}
        <Typography variant="body2">
          © {new Date().getFullYear()} YourStoryWorld Ltd — Company No. 17098564 · All rights reserved.
        </Typography>

        {/* Main nav links */}
        <Typography variant="body2" style={{ marginTop: "8px" }}>
          <a href="/about" style={footerLinkStyle}>
            About Us
          </a>{" "}
          |{" "}
          <a href="/contact" style={footerLinkStyle}>
            Contact
          </a>{" "}
          |{" "}
          <a href="/help" style={footerLinkStyle}>
            Help & FAQs
          </a>
        </Typography>

        {/* Policy links */}
        <Typography variant="body2" style={{ marginTop: 4 }}>
          <a href="/privacy-policy" style={footerLinkStyle}>
            Privacy Policy
          </a>{" "}
          |{" "}
          <a href="/terms" style={footerLinkStyle}>
            Terms of Use
          </a>{" "}
          |{" "}
          <a href="/cookie-policy" style={footerLinkStyle}>
            Cookie Policy
          </a>{" "}
          |{" "}
          <a href="/child-safety" style={footerLinkStyle}>
            Child Safety
          </a>{" "}
          |{" "}
          <a href="/accessibility" style={footerLinkStyle}>
            Accessibility
          </a>{" "}
          |{" "}
          <a href="/data-protection" style={footerLinkStyle}>
            Data Protection &amp; Security
          </a>
        </Typography>

        {/* Disclaimer */}
        <Typography
          variant="caption"
          style={{ marginTop: "10px", display: "block", color: "#ccc" }}
        >
          ⚠️ Disclaimer: The stories generated by YourStoryWorld are created using
          AI and are intended for entertainment and educational purposes only.
          While we strive for accuracy, some content may contain factual or
          narrative inconsistencies.
          <p
            style={{
              fontSize: "0.8rem",
              color: "#e11d48",
              marginTop: "10px",
            }}
          >
            <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety
            filters to ensure all stories remain age-appropriate. Violent,
            explicit, discriminatory, or harmful content is automatically removed
            or rewritten. Minor variance may occur due to the nature of AI
            generation.
          </p>
        </Typography>
      </footer>
    </div>
  );
};

export default AISchoolsPage;
